import { ClipboardList, PackageSearch, Truck } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/cn';

import { Container } from '../common/Container';
import { SectionHeading } from '../common/Section';
import { WhatsAppIcon } from '../common/icons';
import { Button } from '../ui/button';

interface Step {
  /** Lucide icons and the custom WhatsApp glyph share this shape. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const STEPS: Step[] = [
  {
    icon: PackageSearch,
    title: 'Escolha os produtos',
    description: 'Filtre por pet, marca, porte ou necessidade e monte sua lista.',
  },
  {
    icon: ClipboardList,
    title: 'Revise sua lista',
    description: 'Confira tamanhos e quantidades. Sem cadastro, sem formulário.',
  },
  {
    icon: WhatsAppIcon,
    title: 'Envie pelo WhatsApp',
    description: 'A lista chega pronta na conversa. Confirmamos valores e prazo.',
  },
  {
    icon: Truck,
    title: 'Receba em casa',
    description: 'Endereço e pagamento combinados na conversa, com uma pessoa.',
  },
];

export function HowToBuy() {
  return (
    <section id="como-comprar" className="bg-paw-pattern bg-brown-900 py-14 lg:py-20">
      <Container>
        <SectionHeading
          inverse
          eyebrow="Simples assim"
          title="Como comprar na BrucePets"
          description="Você monta a lista no site, a gente fecha no WhatsApp. Sem cadastro, sem cartão, sem burocracia."
          className="mb-12"
        />

        <ol className="relative grid gap-8 md:grid-cols-4 md:gap-6">
          {/* Connector line, desktop only. */}
          <div
            className="absolute top-10 right-[12.5%] left-[12.5%] hidden h-0.5 bg-caramel-500/25 md:block"
            aria-hidden="true"
          />

          {STEPS.map((step, index) => {
            const Icon = step.icon;

            return (
              <li
                key={step.title}
                className="relative flex gap-5 md:flex-col md:items-center md:gap-3 md:text-center"
              >
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      'relative z-10 grid size-16 shrink-0 place-items-center rounded-2xl bg-orange-500 text-white shadow-card md:size-20',
                    )}
                  >
                    <Icon className="size-7 md:size-8" aria-hidden="true" />
                  </span>

                  {/* Connector segment, mobile only. */}
                  {index < STEPS.length - 1 && (
                    <span
                      className="mt-2 w-0.5 flex-1 bg-caramel-500/25 md:hidden"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className="md:contents">
                  <span className="mb-1 inline-grid size-6 place-items-center rounded-full bg-caramel-500 font-display text-xs font-bold text-cream-100 md:-mt-4 md:mb-0 md:h-6">
                    {index + 1}
                  </span>

                  <h3 className="font-display text-base font-bold text-cream-100">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-cream-300">{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-12 flex justify-center">
          <Button asChild size="lg" variant="inverse">
            <Link to="/catalogo">Começar meu pedido</Link>
          </Button>
        </div>
      </Container>
    </section>
  );
}
